import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { appState } from "@/app/appState";
import { client } from "@/api/client.gen";
import { ActionHandler } from "@/components/UI/tools";

// ---------------------------------------------------------------------------
// Shared evaluation-tournament UI.
//
// The Summariser and Chat evaluation pages differ only in what a "prompt", a
// "context" and a "model" are. Everything downstream of generation — tests,
// judges, human voting and Elo results — is identical, so those tabs live here
// and are parameterised by the API base and a few adapter callbacks.
// ---------------------------------------------------------------------------

export type RankingRow = {
    prompt_id: number | null;
    model_id: number | null;
    elo: number;
    wins: number;
    draws: number;
    losses: number;
    games: number;
};

export type RankingView = {
    composite: RankingRow[];
    by_prompt: RankingRow[];
    by_model: RankingRow[];
};

export type EvalTest = {
    id: number;
    organisation_id: number;
    name: string | null;
    evaluation_criteria: string | null;
    active: boolean;
};

export type Judge = { id: string; kind: string; label: string };

export type EvalPairSide = {
    prompt_id: number;
    model_id: number | null;
    [key: string]: unknown;
};

export type EvalNextPair = {
    test_id: number;
    exhausted: boolean;
    remaining: number;
    evaluation_criteria: string | null;
    axis: string | null;
    a: EvalPairSide | null;
    b: EvalPairSide | null;
    [key: string]: unknown;
};

export type EvalJudgeResults<TContext> = {
    judge: string;
    judge_label: string;
    kind: string; // "agent" | "human" | "combined"
    overall: RankingView;
    match_count: number;
} & TContext;

export type EvalApi = <T>(
    path: string,
    body: Record<string, unknown>
) => Promise<T>;

/** Build a POST helper bound to one evaluation API prefix, e.g. "/api/chat-tests". */
export function makeEvalApi(apiBase: string): EvalApi {
    return async function api<T>(
        path: string,
        body: Record<string, unknown>
    ): Promise<T> {
        const response = await client.post({
            url: `${apiBase}/${path}`,
            body,
            headers: { "Content-Type": "application/json" },
        });
        if (response.error) {
            const detail =
                (response.error as { detail?: string })?.detail ?? appState.t("chat.requestFailed");
            throw new Error(detail);
        }
        return response.data as T;
    };
}

export function reportError(e: unknown) {
    appState.setModal({
        title: appState.t("common.error"),
        body: e instanceof Error ? e.message : appState.t("chat.requestFailed"),
    });
}

export const testLabel = (t: EvalTest) => t.name?.trim() || `Test #${t.id}`;

// ---------------------------------------------------------------------------
// Enable / disable — disabled rows keep their cached outputs and match history,
// they are only skipped by future generation and matchmaking.
// ---------------------------------------------------------------------------

/** The kind discriminator accepted by the shared `set-active` endpoint. */
export type ActiveKind = "prompt" | "context" | "model" | "test";

export const InactiveBadge = () => (
    <span className="pill pill-danger">
        off
    </span>
);

export const ActiveToggle = observer(({
    active,
    onToggle,
    disabled,
}: {
    active: boolean;
    onToggle: () => void;
    disabled?: boolean;
}) => (
    <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title={
            active
                ? appState.t("eval.disableHint")
                : appState.t("eval.enableHint")
        }
        className="btn btn-soft btn-sm btn-mono shrink-0"
    >
        {active ? appState.t("common.disable") : appState.t("common.enable")}
    </button>
));

/** Build the `set-active` caller for one resource kind. */
export const makeSetActive =
    (api: EvalApi, kind: ActiveKind) =>
        async (id: number, active: boolean) => {
            await api("set-active", { kind, id, active });
        };

// ---------------------------------------------------------------------------
// Page chrome — title dropdown to switch suites + tab bar
// ---------------------------------------------------------------------------

const EVALUATION_PAGES = [
    { route: "summarisation-tests", labelKey: "eval.tab.summariser" },
    { route: "chat_evaluation", labelKey: "eval.tab.chat" },
];

export const EvaluationHeader = observer(function EvaluationHeader<T extends string>({
    route,
    tabs,
    tab,
    setTab,
}: {
    route: string;
    tabs: readonly T[];
    tab: T;
    setTab: (t: T) => void;
}) {
    const navigate = useNavigate();
    return (
        <div className="flex flex-row items-center justify-between gap-2">
            <select
                className="suite-switcher"
                value={route}
                onChange={(e) => navigate(`/workspace/${e.target.value}`)}
            >
                {EVALUATION_PAGES.map((p) => (
                    <option key={p.route} value={p.route}>
                        {appState.t(p.labelKey)}
                    </option>
                ))}
            </select>
            <nav className="tabs">
                {tabs.map((t) => (
                    <button
                        key={t}
                        type="button"
                        onClick={() => setTab(t)}
                        className={`tab ${tab === t ? "tab-active" : ""}`}
                    >
                        {t}
                    </button>
                ))}
            </nav>
        </div>
    );
});

// ---------------------------------------------------------------------------
// Tests tab — create (auto-runs tournament) + read-only view + delete
// ---------------------------------------------------------------------------

export const TestsTab = observer(({ api }: { api: EvalApi }) => {
    const [rows, setRows] = useState<EvalTest[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftCriteria, setDraftCriteria] = useState("");
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "test");

    const load = async (preserveId?: number) => {
        try {
            const data = await api<EvalTest[]>("list-tests", {});
            setRows(data);
            setSelectedId(preserveId ?? (data.length > 0 ? data[0].id : null));
            setIsNew(false);
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = rows.find((x) => x.id === selectedId) ?? null;

    const addNew = () => {
        setSelectedId(null);
        setIsNew(true);
        setDraftName("");
        setDraftCriteria("");
    };

    const create = async () => {
        setSaving(true);
        try {
            const created = await api<EvalTest>("create-test", {
                name: draftName,
                evaluation_criteria: draftCriteria,
            });
            await load(created.id);
            appState.setModal({
                title: appState.t("eval.tournamentStarted"),
                body: appState.t("eval.tournamentBody"),
            });
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-test", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (t: EvalTest) => {
        setSaving(true);
        try {
            await setActive(t.id, !t.active);
            await load(t.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MasterDetail
            addLabel="+ Add test"
            onAdd={addNew}
            items={rows.map((t) => ({
                id: t.id,
                label: testLabel(t),
                selected: !isNew && t.id === selectedId,
                active: t.active,
                onClick: () => {
                    setSelectedId(t.id);
                    setIsNew(false);
                },
            }))}
            emptyLabel={appState.t("eval.noTests")}
        >
            {isNew ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <p className="admin-note">
                        Saving a test runs its tournament automatically (any prior
                        results are cleared). Tests cannot be edited once saved.
                    </p>
                    <label className="flex flex-col gap-1">
                        <span className="field-label">Name</span>
                        <input
                            className="input input-mono"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            placeholder="e.g. Faithfulness & concision"
                        />
                    </label>
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Evaluation criteria (judge instruction)
                        </span>
                        <textarea
                            className="textarea input-mono flex-1 min-h-[12rem]"
                            value={draftCriteria}
                            onChange={(e) => setDraftCriteria(e.target.value)}
                        />
                    </label>
                    <div className="flex flex-row gap-2 shrink-0">
                        <ActionHandler title={appState.t("eval.saveAndRun")} funq={create} disabled={saving} className="btn-ghost btn-mono">
                            {saving ? appState.t("common.saving") : appState.t("eval.saveAndRun")}
                        </ActionHandler>
                    </div>
                </div>
            ) : selected ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <div className="flex flex-row items-center justify-between gap-2">
                        <h2 className="admin-heading flex flex-row items-center gap-2 min-w-0">
                            <span className="truncate">{testLabel(selected)}</span>
                            {!selected.active && <InactiveBadge />}
                        </h2>
                        <div className="flex flex-row items-center gap-2 shrink-0">
                            <ActiveToggle
                                active={selected.active}
                                onToggle={() => toggleActive(selected)}
                                disabled={saving}
                            />
                            <ActionHandler
                                title={appState.t("common.delete")}
                                funq={() => remove(selected.id)}
                                disabled={saving}
                                className="btn-ghost btn-mono"
                            >
                                {appState.t("common.delete")}
                            </ActionHandler>
                        </div>
                    </div>
                    {!selected.active && (
                        <p className="admin-note">
                            Disabled — this test is skipped by automatic runs and cannot
                            be run manually. Its results are kept.
                        </p>
                    )}
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Evaluation criteria (judge instruction)
                        </span>
                        <textarea
                            readOnly
                            className="textarea input-mono flex-1 min-h-[12rem]"
                            value={selected.evaluation_criteria ?? ""}
                        />
                    </label>
                </div>
            ) : (
                <p className="empty-state">
                    Select a test, or add a new one.
                </p>
            )}
        </MasterDetail>
    );
});

// ---------------------------------------------------------------------------
// Judges tab — read-only list
// ---------------------------------------------------------------------------

export const JudgesTab = ({ api }: { api: EvalApi }) => {
    const [judges, setJudges] = useState<Judge[]>([]);

    useEffect(() => {
        api<Judge[]>("list-judges", {}).then(setJudges).catch(reportError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="panel gap-2 h-full">
            <p className="admin-note">
                Judges decide matches. The agent judge runs automatically when a test
                is saved; humans vote in the Vote tab.
            </p>
            <ul className="flex flex-col">
                {judges.map((j) => (
                    <li key={j.id} className="row">
                        <span className="pill">{j.kind}</span>
                        <span>{j.label}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Vote tab — HotOrNot-style human judging
// ---------------------------------------------------------------------------

export const VoteTab = observer(({
    api,
    contextField,
    describeContext,
    outputNoun,
    renderOutput,
}: {
    api: EvalApi;
    /** Name of the context id field in both the next-pair and cast-vote payloads. */
    contextField: string;
    describeContext: (pair: EvalNextPair) => string;
    outputNoun: string;
    renderOutput: (side: EvalPairSide) => React.ReactNode;
}) => {
    const [tests, setTests] = useState<EvalTest[]>([]);
    const [testId, setTestId] = useState<number | null>(null);
    const [pair, setPair] = useState<EvalNextPair | null>(null);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api<EvalTest[]>("list-tests", {})
            .then((data) => {
                setTests(data);
                if (data.length > 0) setTestId((t) => t ?? data[0].id);
            })
            .catch(reportError);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadPair = async (id: number) => {
        setLoading(true);
        try {
            setPair(await api<EvalNextPair>("next-pair", { test_id: id }));
        } catch (e) {
            reportError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (testId != null) loadPair(testId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);

    const vote = async (winner: "a" | "b" | "draw") => {
        if (testId == null || !pair || pair.exhausted || !pair.a || !pair.b) return;
        setBusy(true);
        try {
            await api("cast-vote", {
                test_id: testId,
                [contextField]: pair[contextField],
                prompt_id_a: pair.a.prompt_id,
                model_id_a: pair.a.model_id,
                prompt_id_b: pair.b.prompt_id,
                model_id_b: pair.b.model_id,
                winner,
            });
            await loadPair(testId);
        } catch (e) {
            reportError(e);
        } finally {
            setBusy(false);
        }
    };

    const clearAll = async () => {
        if (testId == null) return;
        setBusy(true);
        try {
            await api("clear-votes", { test_id: testId });
            await loadPair(testId);
        } catch (e) {
            reportError(e);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="panel gap-3 h-full overflow-y-auto">
            <div className="flex flex-row gap-2 items-end shrink-0">
                <label className="flex flex-col gap-1">
                    <span className="field-label">Test</span>
                    <select
                        className="select input-mono"
                        value={testId ?? ""}
                        onChange={(e) => setTestId(parseInt(e.target.value, 10))}
                    >
                        {tests.map((t) => (
                            <option key={t.id} value={t.id}>
                                {testLabel(t)}
                            </option>
                        ))}
                    </select>
                </label>
                {pair && !pair.exhausted && (
                    <span className="admin-note">
                        {pair.remaining} left · {pair.axis} duel · {describeContext(pair)}
                    </span>
                )}
            </div>

            {loading && <p className="empty-state">{appState.t("common.loading")}</p>}

            {!loading && pair && pair.exhausted && (
                <div className="flex flex-col items-center gap-4">
                    <p className="empty-state">
                        Nothing left to vote on for this test.
                    </p>
                    <button
                        type="button"
                        onClick={clearAll}
                        disabled={busy}
                        className="btn btn-danger-solid btn-mono"
                    >
                        CLEAR ALL VOTES
                    </button>
                </div>
            )}

            {!loading && pair && !pair.exhausted && pair.a && pair.b && (
                <>
                    {pair.evaluation_criteria && (
                        <div className="shrink-0">
                            <span className="field-label">{appState.t("eval.judgementPrompt")}</span>
                            <p className="code-block mt-1 max-h-[6rem]">
                                {pair.evaluation_criteria}
                            </p>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
                        <OutputCard title={`${outputNoun} A`}>
                            {renderOutput(pair.a)}
                        </OutputCard>
                        <OutputCard title={`${outputNoun} B`}>
                            {renderOutput(pair.b)}
                        </OutputCard>
                    </div>
                    <div className="flex flex-row gap-2 justify-center shrink-0">
                        <button type="button" onClick={() => vote("a")} disabled={busy}
                            className="btn btn-soft btn-mono">
                            ◀ A is better
                        </button>
                        <button type="button" onClick={() => vote("draw")} disabled={busy}
                            className="btn btn-soft btn-mono">
                            Draw
                        </button>
                        <button type="button" onClick={() => vote("b")} disabled={busy}
                            className="btn btn-soft btn-mono">
                            B is better ▶
                        </button>
                    </div>
                </>
            )}
        </div>
    );
});

export const OutputCard = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    <div className="card flex flex-col min-h-0">
        <div className="panel-header">
            {title}
        </div>
        <div className="code-block flex-1 min-h-0">
            {children}
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// Results tab
// ---------------------------------------------------------------------------

export type ResultsContextSection = {
    key: number;
    label: string;
    rankings: RankingView;
};

export const ResultsTab = observer(function ResultsTab<TJudge extends { judge: string; judge_label: string; kind: string; overall: RankingView; match_count: number },
    TProgress>({
        api,
        loadLabels,
        perContext,
        progressLine,
        promptHeader = "Prompt",
        modelHeader = "Model",
        nullModelLabel = "production",
    }: {
        api: EvalApi;
        loadLabels: () => Promise<{
            promptNames: Record<number, string>;
            modelNames: Record<number, string>;
        }>;
        perContext: (judge: TJudge) => ResultsContextSection[];
        progressLine: (progress: TProgress, matchCount: number | null) => string;
        promptHeader?: string;
        modelHeader?: string;
        nullModelLabel?: string;
    }) {
    const [tests, setTests] = useState<EvalTest[]>([]);
    const [testId, setTestId] = useState<number | null>(null);
    const [results, setResults] = useState<{
        test_id: number;
        judges: TJudge[];
        match_count: number;
    } | null>(null);
    const [progress, setProgress] = useState<TProgress | null>(null);
    const [promptNames, setPromptNames] = useState<Record<number, string>>({});
    const [modelNames, setModelNames] = useState<Record<number, string>>({});
    const [loading, setLoading] = useState(false);
    const [filling, setFilling] = useState(false);

    const runMissing = async () => {
        if (testId == null) return;
        setFilling(true);
        try {
            await api("fill-missing", { test_id: testId });
            appState.setModal({
                title: appState.t("eval.fillMissing"),
                body: appState.t("eval.fillMissingBody"),
            });
        } catch (e) {
            reportError(e);
        } finally {
            setFilling(false);
        }
    };

    const runMissingAll = async () => {
        setFilling(true);
        try {
            await api("fill-missing-all", {});
            appState.setModal({
                title: appState.t("eval.fillMissing"),
                body: appState.t("eval.fillMissingAllBody"),
            });
        } catch (e) {
            reportError(e);
        } finally {
            setFilling(false);
        }
    };

    const loadTests = async () => {
        try {
            const [data, labels] = await Promise.all([
                api<EvalTest[]>("list-tests", {}),
                loadLabels(),
            ]);
            setTests(data);
            setPromptNames(labels.promptNames);
            setModelNames(labels.modelNames);
            if (data.length > 0) setTestId((t) => t ?? data[0].id);
        } catch (e) {
            reportError(e);
        }
    };

    const loadResults = async (id: number) => {
        setLoading(true);
        try {
            const [res, prog] = await Promise.all([
                api<{ test_id: number; judges: TJudge[]; match_count: number }>(
                    "get-results",
                    { test_id: id }
                ),
                api<TProgress>("progress", {}),
            ]);
            setResults(res);
            setProgress(prog);
        } catch (e) {
            reportError(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadTests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (testId != null) loadResults(testId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [testId]);

    return (
        <div className="panel gap-3 h-full overflow-y-auto">
            <div className="flex flex-row gap-2 items-end shrink-0">
                <label className="flex flex-col gap-1">
                    <span className="field-label">Test</span>
                    <select
                        className="select input-mono"
                        value={testId ?? ""}
                        onChange={(e) => setTestId(parseInt(e.target.value, 10))}
                    >
                        {tests.map((t) => (
                            <option key={t.id} value={t.id}>
                                {testLabel(t)}
                            </option>
                        ))}
                    </select>
                </label>
                <ActionHandler
                    title={appState.t("eval.reload")}
                    funq={() => (testId != null ? loadResults(testId) : Promise.resolve())}
                    disabled={loading}
                    className="btn-ghost btn-mono"
                >
                    {loading ? appState.t("common.loading") : appState.t("eval.reload")}
                </ActionHandler>
            </div>

            {progress && (
                <p className="admin-note">
                    {progressLine(progress, results?.match_count ?? null)}
                </p>
            )}

            <div className="flex flex-row gap-3 self-start">
                <button
                    type="button"
                    onClick={runMissing}
                    disabled={filling || testId == null}
                    title={appState.t("eval.fillMissingTitle")}
                    className="link-muted"
                >
                    {filling ? appState.t("eval.starting") : appState.t("eval.runMissing")}
                </button>
                <button
                    type="button"
                    onClick={runMissingAll}
                    disabled={filling || tests.length === 0}
                    title={appState.t("eval.fillMissingAllTitle")}
                    className="link-muted"
                >
                    {appState.t("eval.runMissingAll")}
                </button>
            </div>

            {results &&
                results.judges.map((jr) => (
                    <div key={jr.judge} className="panel gap-2 shrink-0">
                        <h2 className="admin-heading">
                            {jr.judge_label}{" "}
                            <span className="admin-note">
                                ({jr.kind} · {jr.match_count} matches)
                            </span>
                        </h2>
                        <div>
                            <h3 className="admin-note mb-1">{appState.t("eval.overall")}</h3>
                            <RankingViewTables
                                view={jr.overall}
                                promptNames={promptNames}
                                modelNames={modelNames}
                                promptHeader={promptHeader}
                                modelHeader={modelHeader}
                                nullModelLabel={nullModelLabel}
                            />
                        </div>
                        {perContext(jr).map((section) => (
                            <details key={section.key} className="divider pt-1">
                                <summary className="admin-disclosure">
                                    {section.label}
                                </summary>
                                <div className="mt-1">
                                    <RankingViewTables
                                        view={section.rankings}
                                        promptNames={promptNames}
                                        modelNames={modelNames}
                                        promptHeader={promptHeader}
                                        modelHeader={modelHeader}
                                        nullModelLabel={nullModelLabel}
                                    />
                                </div>
                            </details>
                        ))}
                    </div>
                ))}

            {results && results.match_count === 0 && (
                <p className="empty-state">
                    No matches yet — the tournament may still be running.
                </p>
            )}
        </div>
    );
});

export const RankingViewTables = ({
    view,
    promptNames,
    modelNames,
    promptHeader = "Prompt",
    modelHeader = "Model",
    nullModelLabel = "production",
}: {
    view: RankingView;
    promptNames: Record<number, string>;
    modelNames: Record<number, string>;
    promptHeader?: string;
    modelHeader?: string;
    nullModelLabel?: string;
}) => (
    <div className="flex flex-col gap-2">
        <RankingBlock
            title={`Composite (${promptHeader.toLowerCase()} × ${modelHeader.toLowerCase()})`}
            rows={view.composite}
            promptNames={promptNames}
            modelNames={modelNames}
            promptHeader={promptHeader}
            modelHeader={modelHeader}
            nullModelLabel={nullModelLabel}
            showPrompt
            showModel
        />
        <RankingBlock
            title={`By ${promptHeader.toLowerCase()}`}
            rows={view.by_prompt}
            promptNames={promptNames}
            modelNames={modelNames}
            promptHeader={promptHeader}
            modelHeader={modelHeader}
            nullModelLabel={nullModelLabel}
            showPrompt
        />
        <RankingBlock
            title={`By ${modelHeader.toLowerCase()}`}
            rows={view.by_model}
            promptNames={promptNames}
            modelNames={modelNames}
            promptHeader={promptHeader}
            modelHeader={modelHeader}
            nullModelLabel={nullModelLabel}
            showModel
        />
    </div>
);

const RankingBlock = observer(({
    title,
    rows,
    promptNames,
    modelNames,
    promptHeader,
    modelHeader,
    nullModelLabel,
    showPrompt,
    showModel,
}: {
    title: string;
    rows: RankingRow[];
    promptNames: Record<number, string>;
    modelNames: Record<number, string>;
    promptHeader: string;
    modelHeader: string;
    nullModelLabel: string;
    showPrompt?: boolean;
    showModel?: boolean;
}) => {
    const cols = 6 + (showPrompt ? 1 : 0) + (showModel ? 1 : 0);
    return (
        <div>
            <h4 className="label-xs">
                {title}
            </h4>
            <table className="data-table">
                <thead>
                    <tr>
                        <th>#</th>
                        {showPrompt && <th>{promptHeader}</th>}
                        {showModel && <th>{modelHeader}</th>}
                        <th>{appState.t("eval.elo")}</th>
                        <th>W</th>
                        <th>D</th>
                        <th>L</th>
                        <th>{appState.t("eval.games")}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={`${r.prompt_id}-${r.model_id}`}>
                            <td>{i + 1}</td>
                            {showPrompt && (
                                <td className="truncate max-w-[14rem]">
                                    {r.prompt_id == null
                                        ? "—"
                                        : promptNames[r.prompt_id] ?? `#${r.prompt_id}`}
                                </td>
                            )}
                            {showModel && (
                                <td className="truncate max-w-[14rem]">
                                    {r.model_id == null
                                        ? nullModelLabel
                                        : modelNames[r.model_id] ?? `#${r.model_id}`}
                                </td>
                            )}
                            <td>{r.elo}</td>
                            <td>{r.wins}</td>
                            <td>{r.draws}</td>
                            <td>{r.losses}</td>
                            <td>{r.games}</td>
                        </tr>
                    ))}
                    {rows.length === 0 && (
                        <tr>
                            <td colSpan={cols} className="empty-state">
                                No data
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
});

// ---------------------------------------------------------------------------
// Shared master-detail layout
// ---------------------------------------------------------------------------

export type MasterItem = {
    id: number;
    label: string;
    selected: boolean;
    onClick: () => void;
    active?: boolean;
};

export const MasterDetail = ({
    items,
    addLabel,
    onAdd,
    emptyLabel,
    children,
}: {
    items: MasterItem[];
    addLabel: string;
    onAdd: () => void;
    emptyLabel: string;
    children: React.ReactNode;
}) => (
    <div className="master-detail">
        <aside className="master-detail-list">
            <div className="shrink-0">
                <ActionHandler title={addLabel} funq={onAdd} disabled={false} className="btn-ghost btn-block btn-mono">
                    {addLabel}
                </ActionHandler>
            </div>
            <ul className="flex flex-col gap-[.25rem] flex-1 min-h-0 overflow-y-auto">
                {items.map((it) => (
                    <li key={it.id}>
                        <button
                            type="button"
                            onClick={it.onClick}
                            className={`master-detail-item flex flex-row items-center gap-1 ${it.selected ? "master-detail-item-selected" : ""
                                } ${it.active === false ? "master-detail-item-inactive" : ""}`}
                            title={it.label}
                        >
                            <span className="truncate flex-1">{it.label}</span>
                            {it.active === false && <InactiveBadge />}
                        </button>
                    </li>
                ))}
                {items.length === 0 && (
                    <li className="empty-state">
                        {emptyLabel}
                    </li>
                )}
            </ul>
        </aside>
        <section className="master-detail-main flex flex-col min-h-0 gap-2">
            {children}
        </section>
    </div>
);
